import type { ElementDto, SceneTypeDto } from "@repo/api/overlays";
import type {
  Point,
  Resolution,
  Size,
} from "@/features/Editor/Canvas/canvas-geometry";

const PASTE_OFFSET = 20;

export type ClipboardEntry = {
  element: ElementDto;
  sceneType: SceneTypeDto;
  size: Size;
};

export type Clipboard = {
  resolution: Resolution;
  entries: ClipboardEntry[];
};

// On the same canvas the copy is offset from the original. On another aspect ratio it keeps
// its size and, per axis, its distance to whichever of the start edge, center or end edge
// it is closest to.
export function getPastePosition(
  { element, size }: ClipboardEntry,
  from: Resolution,
  to: Resolution,
): Point {
  const { x, y } = element.position;

  if (from.width === to.width && from.height === to.height) {
    return { x: x + PASTE_OFFSET, y: y + PASTE_OFFSET };
  }

  return {
    x: getAnchoredPosition(x, size.w, from.width, to.width),
    y: getAnchoredPosition(y, size.h, from.height, to.height),
  };
}

function getAnchoredPosition(
  position: number,
  size: number,
  fromLength: number,
  toLength: number,
) {
  const startDistance = position;
  const centerDistance = position + size / 2 - fromLength / 2;
  const endDistance = fromLength - (position + size);

  if (
    startDistance <= Math.abs(centerDistance) &&
    startDistance <= endDistance
  ) {
    return position;
  }

  if (endDistance <= Math.abs(centerDistance)) {
    return Math.round(toLength - size - endDistance);
  }

  return Math.round(toLength / 2 + centerDistance - size / 2);
}
